import { inject, injectable } from 'inversify';
import { TYPES } from '../../di/types';
import type {
  MonitorManager,
  Monitor,
  SentryMessageOptions,
  SLOEvent,
} from './types';
import type { SentryService } from './SentryService';
import type { GrubTelemetryService } from './GrubTelemetryService';

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class MonitorService implements MonitorManager {
  private readonly monitors: Monitor[] = [];

  constructor(
    @inject(TYPES.SentryService) private readonly sentryService: SentryService,
    @inject(TYPES.GrubTelemetryService)
    private readonly grubTelemetryService: GrubTelemetryService,
  ) {
    this.monitors.push(sentryService);
    this.monitors.push(grubTelemetryService);
  }

  public reportError(error: Error): void {
    this.delegateMethod('reportError', error);
  }

  public moduleReady(moduleName: string): void {
    this.delegateMethod('moduleReady', moduleName);
  }

  public setPageAttribute(pageName: string): void {
    this.delegateMethod('setPageAttribute', pageName);
  }

  public initializeUser(): void {
    this.delegateMethod('initializeUser');
  }

  public captureMessage(message: string, options?: SentryMessageOptions): void {
    this.delegateMethod('captureMessage', message, options);
  }

  public sendSLOStartingEvent<T>(sloEvent: SLOEvent<T>): void {
    this.delegateMethod('sendSLOStartingEvent', sloEvent);
  }

  public sendSLOEndingEvent<T>(sloEvent: SLOEvent<T>): void {
    this.delegateMethod('sendSLOEndingEvent', sloEvent);
  }

  private delegateMethod<K extends keyof Monitor>(
    name: K,
    ...args: Parameters<Monitor[K]>
  ) {
    this.monitors.forEach((monitor) => {
      type Method = (...params: typeof args) => void;
      (monitor[name] as Method)?.apply(monitor, args);
    });
  }
}
